import { Circle } from '@mui/icons-material';
import {
  Step,
  StepContent as MuiStepContent,
  StepLabel as MuiStepLabel,
  Stepper as MuiStepper,
  styled,
  Typography,
  Button,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import DraggableList from 'components/DraggableList';
import { Column, Row } from 'further-ui/components/Layout';
import JourneyStep from './JourneyStep';
import useInvestorJourney from './useInvestorJourney';
import { OnboardingStep } from 'further-types/lp/onboarding';
import JourneyStepReview from './JourneyStepReview';
import ManageApplicationDocuments from './StepSettingsComponents/ManageApplicationDocuments';
import CommitmentStepSettings from './CommitmentStepSettings';
import { Vehicle } from 'further-types/lp/vehicle';
import CommitmentDetails from '../CommitmentDetails';
import api from 'lib/trpcClient';
import useVehicleApplication from 'hooks/lp/data/vehicleApplication/useVehicleApplication';
import { useEffect, useRef } from 'react';

const Stepper = styled(MuiStepper)({
  width: '100%',
});

const StepIcon = styled(Circle)(({ theme }) => ({
  color: theme.palette.grey[300],
  fontSize: theme.fontSizes.lg,
  marginLeft: theme.spacing(theme.spacingSizes.xxs),
}));

const StepLabel = styled(MuiStepLabel)(({ theme }) => ({
  padding: 0,

  '& .MuiStepLabel-label': {
    color: theme.palette.grey[600],
    fontSize: theme.fontSizes.regular,
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: '150%',
  },
}));

const StepContent = styled(MuiStepContent)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.lg, 0, 0, theme.spacingSizes.sm),

  '& .MuiCollapse-vertical': {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(theme.spacingSizes.lg),
  },
}));

const JourneySteps = styled(DraggableList)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: theme.spacing(theme.spacingSizes.lg),
}));

const CommitmentDetailsContainer = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(theme.spacingSizes.lg),
}));

type Props = {
  vehicle: Vehicle;
  vehicleApplicationId: string;
  completeCommitmentFormVisible: boolean;
  onHideCompleteCommitmentForm: () => void;
};

const InvestorJourney: React.FC<Props> = ({
  vehicle,
  vehicleApplicationId,
  completeCommitmentFormVisible,
  onHideCompleteCommitmentForm,
}) => {
  const commitmentFormRef = useRef<HTMLDivElement>(null);
  const { vehicleApplication, taxDetails, investor, updateInvestorJourney } =
    useVehicleApplication(vehicle._id, vehicleApplicationId);
  const { data: documentsList, isFetching: isFetchingDocumentsList } =
    api.vehicleSigningDocument.list.useQuery({ vehicleId: vehicle._id });

  const { steps, handleToggleStep, handleStepsOrderChange } =
    useInvestorJourney({
      application: vehicleApplication,
      updateInvestorJourney,
      isDocumentsListEmpty:
        !isFetchingDocumentsList && documentsList?.results.length === 0,
    });

  const [lpFirm] = api.lpFirm.getCurrentFirm.useSuspenseQuery();

  useEffect(() => {
    if (!completeCommitmentFormVisible) {
      return;
    }

    commitmentFormRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    });
  }, [completeCommitmentFormVisible]);

  const handleReorder = ({
    orderedItemIds,
  }: {
    orderedItemIds: Array<string>;
  }) => {
    handleStepsOrderChange(orderedItemIds as Array<OnboardingStep>);
  };

  return (
    <Column spacing="lg" fullWidth>
      {vehicleApplication.commitment ? (
        <>
          <CommitmentDetails
            title="Commitment details confirmation"
            application={vehicleApplication}
            vehicle={vehicle}
            onCommitmentComplete={onHideCompleteCommitmentForm}
            isInvestorJourneyForm
          />
          <Divider />
        </>
      ) : completeCommitmentFormVisible ? (
        <>
          <Card variant="outlined" ref={commitmentFormRef}>
            <CommitmentDetailsContainer>
              <CommitmentDetails
                title="Commitment details confirmation"
                application={vehicleApplication}
                vehicle={vehicle}
                onCommitmentComplete={onHideCompleteCommitmentForm}
                isInvestorJourneyForm
              />
              <Row centered fullWidth spacing="sm" justify="flex-end">
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={onHideCompleteCommitmentForm}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  type="submit"
                  form="commitment-settings-form"
                >
                  Add commitment
                </Button>
              </Row>
            </CommitmentDetailsContainer>
          </Card>
          <Divider />
        </>
      ) : null}
      <Column spacing="xs">
        <Typography variant="h5">Investor journey</Typography>
        <Typography variant="body2">
          Requests define the steps an investor must complete as part of
          onboarding. You can enable requests below, then drag and drop them to
          set their order within the investor journey.
        </Typography>
      </Column>
      <Row>
        <Stepper orientation="vertical">
          <Step>
            <StepLabel slots={{ stepIcon: StepIcon }}>
              Investor journey start
            </StepLabel>
            <StepContent>
              <JourneySteps
                onReorder={handleReorder}
                disabled={!!vehicleApplication.commitment}
              >
                {steps.map((step, index) => (
                  <DraggableList.Item
                    id={step.key}
                    key={step.key}
                    withDragHandle
                  >
                    <JourneyStep
                      number={index + 1}
                      title={step.title}
                      toggleable={step.toggleable}
                      stepKey={step.key}
                      toggleTooltip={step.toggleTooltip}
                      onToggle={handleToggleStep}
                      isEnabled={step.isEnabled}
                      disabled={step.disabled}
                      disabledTooltip={step.disabledTooltip}
                      disableDragHandle={!!vehicleApplication.commitment}
                      settings={
                        <>
                          {step.key ===
                          OnboardingStep.CommitmentConfirmation ? (
                            <CommitmentStepSettings
                              vehicleApplicationId={vehicleApplicationId}
                              vehicle={vehicle}
                            />
                          ) : step.key === OnboardingStep.DocumentSigning ? (
                            <ManageApplicationDocuments
                              application={vehicleApplication}
                              accountType={investor.accountType}
                            />
                          ) : null}
                          {step.showStatus ? (
                            <JourneyStepReview
                              step={step}
                              application={vehicleApplication}
                              taxDetails={taxDetails}
                              firmDomain={lpFirm.portalSubdomain}
                              showDetails={step.showReview}
                            />
                          ) : null}
                        </>
                      }
                    >
                      {step.details ? (
                        <Typography variant="body2">{step.details}</Typography>
                      ) : null}
                    </JourneyStep>
                  </DraggableList.Item>
                ))}
              </JourneySteps>
            </StepContent>
          </Step>
          <Step active={false}>
            <StepLabel slots={{ stepIcon: StepIcon }}>
              Investor journey end
            </StepLabel>
          </Step>
        </Stepper>
      </Row>
    </Column>
  );
};

export default InvestorJourney;
